import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import './Clockify.css'
import TeamHeader from '../TeamHeader/TeamHeader'

import {
    PiMagnifyingGlass,
    PiUsers,
    PiClock,
    PiCheckCircleFill,
    PiXCircleFill,
    PiUserCircle,
    PiCaretDown,
    PiCaretRight,
    PiChartBar,
    PiBell,
    PiX,
} from "react-icons/pi";
import {
    TbCircleLetterMFilled,
    TbCircleLetterTFilled,
    TbCircleLetterWFilled,
    TbCircleLetterFFilled,
} from "react-icons/tb";
import type { IconType } from "react-icons";

/* ─────────────────────────────────────────────────────────── */

interface ClockifyProject {
    project: string;
    time_seconds: number;
    time: string;
}

interface ClockifyUserReport {
    user: string;
    email: string | null;
    Monday: boolean;
    Tuesday: boolean;
    Wednesday: boolean;
    Thursday: boolean;
    Friday: boolean;
    total_time_seconds: number;
    total_time: string;
    projects: ClockifyProject[];
}

// Locally-generated id, since `email` can be null/missing for some users
// and can't be relied on as a unique key/identifier on its own.
interface ClockifyUserReportWithId extends ClockifyUserReport {
    _id: string;
}

type WeekdayKey = "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday";

const WEEKDAYS: { key: WeekdayKey; short: string; Icon: IconType }[] = [
    { key: "Monday", short: "M", Icon: TbCircleLetterMFilled },
    { key: "Tuesday", short: "T", Icon: TbCircleLetterTFilled },
    { key: "Wednesday", short: "W", Icon: TbCircleLetterWFilled },
    { key: "Thursday", short: "T", Icon: TbCircleLetterTFilled },
    { key: "Friday", short: "F", Icon: TbCircleLetterFFilled },
];

const getTodayWeekdayKey = (): WeekdayKey | null => {
    const day = new Date().toLocaleDateString("en-US", { weekday: "long" });
    return (WEEKDAYS.some(w => w.key === day) ? (day as WeekdayKey) : null);
};

const formatHours = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
};

/* ── Portal tooltip badge (weekday dots) ───────────────────── */

interface TooltipState {
    text: string;
    x: number;
    y: number;
}

const DayBadge = ({
    logged,
    isToday,
    tooltip,
    children,
}: {
    logged: boolean;
    isToday: boolean;
    tooltip: string;
    children: React.ReactNode;
}) => {
    const [tip, setTip] = useState<TooltipState | null>(null);

    const handleMouseEnter = useCallback((e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setTip({
            text: tooltip,
            x: rect.left + rect.width / 2,
            y: rect.top,
        });
    }, [tooltip]);

    const handleMouseLeave = useCallback(() => setTip(null), []);

    return (
        <>
            <span
                className={`day-badge ${logged ? "logged" : "not-logged"} ${isToday ? "today" : ""}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {children}
            </span>

            {tip && ReactDOM.createPortal(
                <div
                    className="ob-tooltip"
                    style={{ left: tip.x, top: tip.y }}
                >
                    {tip.text}
                </div>,
                document.body
            )}
        </>
    );
};

/* ─────────────────────────────────────────────────────────── */

const Clockify = () => {

    const [users, setUsers] = useState<ClockifyUserReportWithId[]>([])
    const [weekStart, setWeekStart] = useState<string | null>(null)
    const [weekEnd, setWeekEnd] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [search, setSearch] = useState("")
    const [expandedUsers, setExpandedUsers] = useState<string[]>([]);

    // Notify modal
    const [notifyUser, setNotifyUser] = useState<ClockifyUserReportWithId | null>(null)
    const [selectedDays, setSelectedDays] = useState<WeekdayKey[]>([])
    const [sendingNotify, setSendingNotify] = useState(false)
    const [notifyError, setNotifyError] = useState<string | null>(null)
    const [notifySuccess, setNotifySuccess] = useState<string | null>(null)

    // CSRF
    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin", // include cookies in the request
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    useEffect(() => {
        const fetchClockifyReport = async () => {
            try {
                setLoading(true);
                setError(null);

                const res = await fetch(
                    '/api/clockify/report/',
                    { credentials: "same-origin" }
                );
                const raw = await res.json();

                // Endpoint may return a bare array of user rows, or an
                // object with { week_start, week_end, users }.
                let rows: ClockifyUserReport[];
                if (Array.isArray(raw)) {
                    rows = raw;
                    setWeekStart(null);
                    setWeekEnd(null);
                } else {
                    rows = Array.isArray(raw?.users) ? raw.users : [];
                    setWeekStart(raw?.week_start ?? null);
                    setWeekEnd(raw?.week_end ?? null);
                }

                const withIds: ClockifyUserReportWithId[] = rows.map((u, index) => ({
                    ...u,
                    _id: `${u.email ?? u.user}-${index}`,
                }));

                setUsers(withIds);
            } catch (err) {
                console.error('Failed fetching Clockify report:', err);
                setError('Failed to load Clockify data.');
                setUsers([]);
            } finally {
                setLoading(false);
            }
        };
        fetchClockifyReport();
    }, []);

    const toggleUser = (id: string) => {
        setExpandedUsers(prev =>
            prev.includes(id)
                ? prev.filter(x => x !== id)
                : [...prev, id]
        );
    };

    const openNotifyModal = (user: ClockifyUserReportWithId) => {
        // Default to pre-selecting the days they haven't logged yet --
        // that's usually exactly what you're sending the reminder for.
        const missingDays = WEEKDAYS
            .map(d => d.key)
            .filter(key => !user[key]);

        setNotifyUser(user);
        setSelectedDays(missingDays);
        setNotifyError(null);
        setNotifySuccess(null);
    };

    const closeNotifyModal = () => {
        setNotifyUser(null);
        setSelectedDays([]);
        setNotifyError(null);
        setNotifySuccess(null);
        setSendingNotify(false);
    };

    const toggleNotifyDay = (day: WeekdayKey) => {
        setSelectedDays(prev =>
            prev.includes(day)
                ? prev.filter(d => d !== day)
                : [...prev, day]
        );
    };

    const sendNotification = async () => {
        if (!notifyUser || !notifyUser.email || selectedDays.length === 0) return;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            setSendingNotify(true);
            setNotifyError(null);
            setNotifySuccess(null);

            const orderedDays = WEEKDAYS
                .map(d => d.key)
                .filter(key => selectedDays.includes(key));

            const res = await fetch(
                '/api/clockify/notify/',
                {
                    method: "POST",
                    credentials: "same-origin",
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken
                    },
                    body: JSON.stringify({
                        email: notifyUser.email,
                        days: orderedDays,
                    }),
                }
            );

            if (!res.ok) {
                const data = await res.json().catch(() => null);
                throw new Error(data?.error || "Failed to send reminder.");
            }

            setNotifySuccess(`Reminder sent to ${notifyUser.user}.`);
        } catch (err) {
            console.error('Failed to send Clockify reminder:', err);
            setNotifyError(err instanceof Error ? err.message : "Failed to send reminder.");
        } finally {
            setSendingNotify(false);
        }
    };

    const todayKey = getTodayWeekdayKey();

    const filteredUsers = useMemo(() => {
        const q = search.toLowerCase()
        return users.filter(u =>
            u.user.toLowerCase().includes(q) ||
            (u.email ?? "").toLowerCase().includes(q)
        )
    }, [search, users])

    const totalSeconds = users.reduce((a, b) => a + b.total_time_seconds, 0)
    const loggedToday = todayKey ? users.filter(u => u[todayKey]).length : 0

    // Team-wide project breakdown, aggregated across everyone this week.
    const teamProjects = useMemo(() => {
        const totals = new Map<string, number>();
        users.forEach(u => {
            u.projects.forEach(p => {
                totals.set(p.project, (totals.get(p.project) ?? 0) + p.time_seconds);
            });
        });
        const list = Array.from(totals.entries())
            .map(([project, seconds]) => ({ project, seconds }))
            .sort((a, b) => b.seconds - a.seconds);
        const max = list.length > 0 ? list[0].seconds : 0;
        return { list, max };
    }, [users]);

    return (
        <div className="customer-directory-body">

            <TeamHeader title="Human Resources" />

            <div className="support-container">

                <div className="support-page-header">
                    <div>
                        <h1 className="support-page-title">
                            Hour Log
                        </h1>
                        <p className="support-page-sub">
                            Weekly Clockify hours for everyone who&apos;s clocked time in the last 15 days.
                        </p>
                    </div>
                </div>

                {/* Stats */}

                <div className="account-stats">

                    <div className="account-stat-card">
                        <PiUsers />
                        <span>{users.length}</span>
                        <small>Active Users</small>
                    </div>

                    <div className="account-stat-card">
                        <PiCheckCircleFill />
                        <span>{loggedToday}</span>
                        <small>Logged Today</small>
                    </div>

                    <div className="account-stat-card">
                        <PiXCircleFill />
                        <span>{todayKey ? users.length - loggedToday : 0}</span>
                        <small>May Need Clockify Reminder</small>
                    </div>

                    <div className="account-stat-card">
                        <PiClock />
                        <span>{formatHours(totalSeconds)}</span>
                        <small>Team Hours This Week</small>
                    </div>

                </div>

                {/* Team project breakdown */}

                {!loading && !error && teamProjects.list.length > 0 && (
                    <div className="clockify-team-projects">

                        <div className="clockify-team-projects-header">
                            <PiChartBar />
                            <span>Team Hours by Project</span>
                        </div>

                        <div className="clockify-project-bars">
                            {teamProjects.list.map(p => (
                                <div key={p.project} className="clockify-project-bar-row">
                                    <span className="clockify-project-bar-label">{p.project}</span>
                                    <div className="clockify-project-bar-track">
                                        <div
                                            className="clockify-project-bar-fill"
                                            style={{
                                                width: `${teamProjects.max > 0
                                                    ? (p.seconds / teamProjects.max) * 100
                                                    : 0}%`
                                            }}
                                        />
                                    </div>
                                    <span className="clockify-project-bar-time">{formatHours(p.seconds)}</span>
                                </div>
                            ))}
                        </div>

                    </div>
                )}

                {/* Search */}

                <div className="account-toolbar">
                    <div className="account-search">
                        <PiMagnifyingGlass />
                        <input
                            placeholder="Search users..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {loading && (
                    <p className="support-page-sub">Loading Clockify data...</p>
                )}

                {!loading && error && (
                    <p className="support-page-sub">{error}</p>
                )}

                {/* Users */}

                {!loading && !error && (

                    <div className="account-user-list">

                        {filteredUsers.map(user => {

                            const expanded = expandedUsers.includes(user._id);
                            const loggedTodayFlag = todayKey ? user[todayKey] : false;

                            const sortedProjects = user.projects
                                .slice()
                                .sort((a, b) => b.time_seconds - a.time_seconds);

                            const userMax = sortedProjects.length > 0
                                ? sortedProjects[0].time_seconds
                                : 0;

                            return (
                                <div
                                    key={user._id}
                                    className={`account-user-card ${expanded ? "expanded" : ""}`}
                                >

                                    <div
                                        className="account-user-header"
                                        onClick={() => toggleUser(user._id)}
                                    >

                                        <div className="account-user-left">

                                            <div className="account-avatar">
                                                <PiUserCircle />
                                            </div>

                                            <div className='account-user-left-name'>
                                                <div className="account-user-name-row">
                                                    <h3>{user.user}</h3>
                                                    <div className="day-badges">
                                                        {WEEKDAYS.map(day => (
                                                            <DayBadge
                                                                key={day.key}
                                                                logged={user[day.key]}
                                                                isToday={todayKey === day.key}
                                                                tooltip={`${day.key} — ${user[day.key] ? "Logged" : "Not logged"}`}
                                                            >
                                                                <day.Icon />
                                                            </DayBadge>
                                                        ))}
                                                    </div>
                                                </div>
                                                <p>{user.email ?? "No email on file"}</p>
                                            </div>

                                        </div>

                                        <div className="account-user-summary">

                                            <div className={`today-hours-pill ${todayKey ? (loggedTodayFlag ? "success" : "danger") : "neutral"}`}>
                                                {todayKey
                                                    ? (loggedTodayFlag ? <PiCheckCircleFill /> : <PiXCircleFill />)
                                                    : <PiClock />}
                                                <span>{todayKey ? "Logged Today" : "Weekend"}</span>
                                            </div>

                                            <div className="hours-pill">
                                                {user.total_time}
                                            </div>

                                            {expanded ? (
                                                <PiCaretDown className="expand-icon" />
                                            ) : (
                                                <PiCaretRight className="expand-icon" />
                                            )}

                                        </div>

                                    </div>

                                    {expanded && (
                                        <div className="onboarding-section">

                                            <div className="onboarding-section-header">
                                                <PiChartBar />
                                                <span>Project Breakdown</span>
                                                <span className="onboarding-section-status complete">
                                                    {user.total_time} total
                                                </span>
                                            </div>

                                            {sortedProjects.length === 0 ? (
                                                <p className="support-page-sub">No project time logged this week.</p>
                                            ) : (
                                                <div className="clockify-project-bars">
                                                    {sortedProjects.map(p => (
                                                        <div key={p.project} className="clockify-project-bar-row">
                                                            <span className="clockify-project-bar-label">{p.project}</span>
                                                            <div className="clockify-project-bar-track">
                                                                <div
                                                                    className="clockify-project-bar-fill"
                                                                    style={{
                                                                        width: `${userMax > 0
                                                                            ? (p.time_seconds / userMax) * 100
                                                                            : 0}%`
                                                                    }}
                                                                />
                                                            </div>
                                                            <span className="clockify-project-bar-time">{p.time}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                        </div>
                                    )}

                                    {expanded && (
                                        <div className="account-actions clockify-user-actions">
                                            <button
                                                className="btn-ghost"
                                                disabled={!user.email}
                                                title={user.email ? undefined : "No email on file"}
                                                onClick={() => openNotifyModal(user)}
                                            >
                                                <PiBell />
                                                Notify
                                            </button>
                                        </div>
                                    )}

                                </div>
                            );

                        })}

                    </div>

                )}

            </div>

            {/* Notify Modal */}

            {notifyUser && (
                <div
                    className="modal-overlay"
                    onClick={closeNotifyModal}
                >
                    <div
                        className="modal-box account-password-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <h3 className="modal-title">Send Clockify Reminder</h3>
                            <button
                                className="modal-close-btn"
                                onClick={closeNotifyModal}
                            >
                                <PiX />
                            </button>
                        </div>

                        <div className="modal-body">

                            <p className="support-page-sub">
                                Choose which day(s) to remind <strong>{notifyUser.user}</strong> about.
                            </p>

                            <div className="notify-day-picker">
                                {WEEKDAYS.map(day => {
                                    const active = selectedDays.includes(day.key);
                                    return (
                                        <button
                                            key={day.key}
                                            type="button"
                                            className={`notify-day-chip ${active ? "active" : ""}`}
                                            onClick={() => toggleNotifyDay(day.key)}
                                        >
                                            <day.Icon />
                                            <span>{day.key}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {notifyError && (
                                <p className="notify-feedback error">{notifyError}</p>
                            )}

                            {notifySuccess && (
                                <p className="notify-feedback success">{notifySuccess}</p>
                            )}

                        </div>

                        <div className="modal-actions">
                            <button
                                className="btn-ghost"
                                onClick={closeNotifyModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={sendNotification}
                                disabled={selectedDays.length === 0 || sendingNotify}
                            >
                                <PiBell />
                                {sendingNotify ? "Sending..." : "Send Reminder"}
                            </button>
                        </div>

                    </div>
                </div>
            )}

        </div>
    )
}

export default Clockify